"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert, Cpu, Wrench, FileCheck2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  icon: ShieldAlert,
  tag: "SPEC 01 // DEDICATED SALES",
  title: "Equipment sales focus",
  description: "We focus strictly on commercial machinery sales and factory support. No party rentals, no event staging distractions.",
  detail: "100% venue and operator oriented"
}, {
  icon: FileCheck2,
  tag: "SPEC 02 // LOGS & INSPECTION",
  title: "Transparent unit condition",
  description: "Pre-owned bulls ship with bench-test run sheets, recorded inverter operational hours, and macro frame inspection photos.",
  detail: "Individual unit diagnostic records"
}, {
  icon: Cpu,
  tag: "SPEC 03 // HEAVY ENGINEERING",
  title: "Commercial machinery standards",
  description: "Engineered with industrial variable frequency drives, heavy-duty gearboxes, and reinforced 18oz double-stitched vinyl arenas.",
  detail: "UL-listed power components"
}, {
  icon: Wrench,
  tag: "SPEC 04 // DOMESTIC INVENTORY",
  title: "Stocked parts pipeline",
  description: "Replacement motion sensors, safety stop buttons, wireless consoles, and hydraulic seals dispatched directly from our U.S. warehouse.",
  detail: "Same-day dispatch on critical spares"
}];
const facilityMetrics = [{
  label: "ELECTRICAL TESTING",
  value: "220V / 110V Bench Validated"
}, {
  label: "FRAME INTEGRITY",
  value: "Reinforced Structural Steel"
}, {
  label: "SAFETY SHUTOFF",
  value: "Fail-safe Dual Mushroom Stops"
}, {
  label: "LOGISTICS READY",
  value: "Dedicated Freight Crating"
}];
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-us" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-14 border-b border-border pb-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="font-mono text-xs font-semibold tracking-widest text-primary uppercase">
                COMMERCIAL DIFFERENTIATORS // MBS INDUSTRIAL
              </span>
              <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight uppercase sm:text-4xl lg:text-5xl">
                Built for commercial venues, not party rentals
              </h2>
              <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                Inspect verified motor specs, review certified run-hours on used inventory,
                and get direct factory support for every bull system we distribute.
              </p>
            </div>

            <div className="mt-4 md:mt-0">
              <Button asChild variant="outline" className="h-11 rounded-none border-foreground px-6 font-serif text-base font-semibold tracking-wide uppercase transition-[background-color,color] hover:bg-foreground hover:text-background">
                <Link to="/SetupSafetyAndMaintenancePage">
                  View Setup & Standards
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Four-Card Feature Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <motion.div key={feature.title} variants={itemVariants} data-index={i}>
                <Card className="h-full rounded-none border border-border bg-card p-6 shadow-xs transition-[border-color,background-color] hover:border-foreground">
                  <CardHeader className="p-0">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex size-11 items-center justify-center border border-border bg-muted text-foreground">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <span className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground">
                        0{i + 1}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                      {feature.tag}
                    </span>
                    <CardTitle className="mt-2 font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="mt-4 flex flex-1 flex-col justify-between p-0">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {feature.description}
                    </p>

                    <div className="mt-6 border-t border-border pt-4">
                      <div className="flex items-center gap-2 font-mono text-xs text-foreground">
                        <CheckCircle2 className="size-3.5 shrink-0 text-primary" />
                        <span>{feature.detail}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Industrial Facility & Inspection Blueprint Spotlight */}
        <div className="mt-14 border border-border bg-muted p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            {/* Visual Plate */}
            <div className="relative border border-border bg-background p-2 lg:col-span-6">
              <div className="relative aspect-16/10 overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_bull_inspection_facility.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_bull_inspection_facility.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_bull_inspection_facility.webp-wvc-sizes" alt="Industrial mechanical bull assembly and testing floor" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 bg-secondary px-2.5 py-1 font-mono text-[11px] font-medium tracking-wider text-secondary-foreground uppercase">
                  FACILITY DIAGNOSTICS // VERIFIED
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between px-2 font-mono text-xs text-muted-foreground">
                <span>STATION: ASSEMBLY BAY 03</span>
                <span>CALIBRATION: PASSED</span>
              </div>
            </div>

            {/* Spec Sheet Table */}
            <div className="flex flex-col justify-center lg:col-span-6">
              <span className="font-mono text-xs font-semibold tracking-widest text-primary uppercase">
                EQUIPMENT PROTOCOL
              </span>
              <h3 className="mt-1 font-serif text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                Rigorous testing before nationwide freight
              </h3>
              <p className="mt-3 text-sm text-muted-foreground sm:text-base">
                Every commercial system undergoes continuous rotational stress runs, emergency brake
                cycling, and pressure monitoring before leaving our loading dock.
              </p>

              {/* Data Table */}
              <div className="mt-6 border border-border bg-card">
                {facilityMetrics.map((metric, i) => <div key={metric.label} data-index={i} className={`flex items-center justify-between px-4 py-2.5 text-xs sm:text-sm ${i !== facilityMetrics.length - 1 ? "border-b border-border" : ""} ${i % 2 === 1 ? "bg-muted/50" : "bg-card"}`}>
                    <span className="font-mono text-xs font-medium text-muted-foreground uppercase">
                      {metric.label}
                    </span>
                    <span className="font-mono text-xs font-semibold text-foreground">
                      {metric.value}
                    </span>
                  </div>)}
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Button asChild className="h-11 rounded-none border border-primary bg-primary px-6 font-serif text-base font-semibold tracking-wide uppercase text-primary-foreground transition-[background-color,border-color] hover:bg-primary/90">
                  <Link to="/ContactPage">Request Spec Sheets & Quotes</Link>
                </Button>
                <Button asChild variant="outline" className="h-11 rounded-none border-border bg-background px-6 font-serif text-base font-semibold tracking-wide uppercase text-foreground hover:bg-accent hover:text-accent-foreground">
                  <Link to="/ShopPage">Browse Bull Inventory</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}